import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield,
  Globe,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Settings,
  Bell,
  LayoutDashboard,
  Puzzle,
  Palette,
  Clock,
  ChevronRight,
  Zap,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import Layout from "../components/layout/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [conflicts, setConflicts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, sitesRes, alertsRes, conflictsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/sites`),
        axios.get(`${API}/alerts?status=active`),
        axios.get(`${API}/conflicts`),
      ]);
      setStats(statsRes.data);
      setSites(sitesRes.data);
      setAlerts(alertsRes.data);
      setConflicts(conflictsRes.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching data:", err);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/sites/check-all`);
      toast.success("All sites checked successfully");
      await fetchData();
    } catch (error) {
      toast.error("Failed to check sites");
    } finally {
      setRefreshing(false);
    }
  };

  const handleCheckSite = async (siteId) => {
    try {
      await axios.post(`${API}/sites/${siteId}/check`);
      toast.success("Site checked successfully");
      await fetchData();
    } catch (error) {
      toast.error("Failed to check site");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-400">Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-50 font-['Outfit'] tracking-tight">
              Live Site Protection
            </h1>
            <p className="text-zinc-500 mt-1">
              We monitor your WordPress sites 24/7 and alert you the moment something breaks.
            </p>
          </div>
          <Button
            data-testid="refresh-all-btn"
            onClick={handleRefreshAll}
            disabled={refreshing}
            className="sheriff-btn-primary h-10 px-4"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Checking..." : "Check All Sites"}
          </Button>
        </div>

        {/* Status line */}
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
          <p className="text-sm text-zinc-500">Monitoring active — last check just now.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="sheriff-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">
                    Total Sites
                  </p>
                  <p className="text-3xl font-bold text-slate-50 font-mono">
                    {stats?.total_sites || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-md bg-zinc-800 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-zinc-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="sheriff-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">
                    Online
                  </p>
                  <p className="text-3xl font-bold text-emerald-500 font-mono">
                    {stats?.online_sites || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-md bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="sheriff-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">
                    Offline
                  </p>
                  <p className="text-3xl font-bold text-red-500 font-mono">
                    {stats?.offline_sites || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-md bg-red-500/10 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="sheriff-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">
                    Active Alerts
                  </p>
                  <p className="text-3xl font-bold text-amber-500 font-mono">
                    {stats?.active_alerts || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-md bg-amber-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Updates Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="sheriff-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-md bg-amber-500/10 flex items-center justify-center">
                  <Puzzle className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">
                    Plugin Updates Available
                  </p>
                  <p className="text-2xl font-bold text-slate-50 font-mono">
                    {stats?.total_plugin_updates || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="sheriff-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-md bg-amber-500/10 flex items-center justify-center">
                  <Palette className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">
                    Theme Updates Available
                  </p>
                  <p className="text-2xl font-bold text-slate-50 font-mono">
                    {stats?.total_theme_updates || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Conflict Risk Detection */}
        {conflicts && conflicts.sites_with_conflicts > 0 && (
          <div className="space-y-4" data-testid="conflict-detection-section">
            {conflicts.results.map((siteConflict) =>
              siteConflict.conflicts.map((conflict, idx) => (
                <Card
                  key={`${siteConflict.site_id}-${idx}`}
                  data-testid={`conflict-card-${siteConflict.site_id}-${idx}`}
                  className="bg-amber-500/[0.04] border border-amber-500/20 rounded-md overflow-hidden"
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-md bg-amber-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Zap className="w-5 h-5 text-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-sm font-semibold text-amber-500 uppercase tracking-wider">
                            Potential Conflict Detected
                          </h3>
                          <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-xs">
                            {conflict.category}
                          </Badge>
                        </div>
                        <p className="text-sm text-zinc-400 mb-3">
                          <span className="text-slate-50 font-medium">{siteConflict.site_name}</span>
                          {" — "}{conflict.message}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {conflict.plugins.map((pluginName) => (
                            <Badge
                              key={pluginName}
                              variant="outline"
                              className="bg-zinc-900 text-slate-50 border-zinc-700 font-mono text-xs"
                            >
                              {pluginName}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Sites and Alerts */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Sites List */}
          <div className="xl:col-span-2">
            <Card className="sheriff-card">
              <CardHeader className="border-b border-zinc-800 pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-slate-50 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-amber-500" />
                    Monitored Sites
                  </CardTitle>
                  <Link to="/sites">
                    <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                      View All <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {sites.length === 0 ? (
                  <div className="p-8 text-center">
                    <Globe className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-500 mb-4">No sites added yet</p>
                    <Link to="/sites">
                      <Button className="sheriff-btn-primary">Add Your First Site</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-800">
                    {sites.slice(0, 5).map((site) => (
                      <div
                        key={site.site_id}
                        data-testid={`site-row-${site.site_id}`}
                        className="p-4 hover:bg-zinc-800/50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div
                              className={`status-dot ${
                                site.is_online ? "status-dot-online" : "status-dot-offline"
                              }`}
                            ></div>
                            <div>
                              <p className="font-medium text-slate-50">{site.site_name}</p>
                              <p className="text-sm text-zinc-500 truncate max-w-[300px]">
                                {site.site_url}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {(site.site_type || "wordpress") === "wordpress" && site.wp_version && (
                              <Badge variant="outline" className="badge-warning font-mono text-xs">
                                WP {site.wp_version}
                              </Badge>
                            )}
                            {(site.site_type || "wordpress") === "wordpress" && site.plugin_updates > 0 && (
                              <Badge variant="outline" className="badge-warning">
                                {site.plugin_updates} plugins
                              </Badge>
                            )}
                            {(site.site_type || "wordpress") === "generic" && (
                              <Badge variant="outline" className="bg-zinc-800 text-zinc-400 border-zinc-700 text-xs">
                                Website
                              </Badge>
                            )}
                            <Button
                              data-testid={`check-site-${site.site_id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCheckSite(site.site_id)}
                              className="text-zinc-400 hover:text-amber-500"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        {site.last_error && (
                          <p className="mt-2 text-sm text-red-400 pl-7">{site.last_error}</p>
                        )}
                        {site.last_checked && (
                          <p className="mt-1 text-xs text-zinc-600 pl-7 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Last checked: {new Date(site.last_checked).toLocaleString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Active Alerts */}
          <div>
            <Card className="sheriff-card">
              <CardHeader className="border-b border-zinc-800 pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-slate-50 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-amber-500" />
                    Active Alerts
                  </CardTitle>
                  <Link to="/alerts">
                    <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                      View All <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {alerts.length === 0 ? (
                  <div className="p-8 text-center">
                    <CheckCircle className="w-12 h-12 text-emerald-500/30 mx-auto mb-4" />
                    <p className="text-zinc-500">All systems operational</p>
                    <p className="text-xs text-zinc-600 mt-2">If this site goes down, you'll be notified instantly.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-800">
                    {alerts.slice(0, 5).map((alert) => (
                      <div
                        key={alert.alert_id}
                        data-testid={`alert-row-${alert.alert_id}`}
                        className="p-4 hover:bg-zinc-800/50 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`mt-1 w-2 h-2 rounded-full ${
                              alert.severity === "critical"
                                ? "bg-red-500"
                                : alert.severity === "warning"
                                ? "bg-amber-500"
                                : "bg-blue-500"
                            }`}
                          ></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-50">
                              {alert.site_name}
                            </p>
                            <p className="text-sm text-zinc-400 truncate">{alert.message}</p>
                            <p className="text-xs text-zinc-600 mt-1">
                              {new Date(alert.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
